import WebService from "./WebService";

export interface InvoiceDoc {
  id?: number;
  invoice_id?: number;
  invoice_number?: string;
  sale_order_id?: number;
  invoice_date?: string;
  due_date?: string;
  discount_amount?: number;
  tax_amount?: number;
  subtotal?: number;
  additional_charges?: number;
  total_amount?: number;
  grand_total?: number;
  status?: string;
  notes?: string;
  pdf_path?: string;
  created_at?: string;
}

export interface ReceiptDoc {
  receipt_id?: number;
  receipt_number?: string;
  payment_record_id?: number;
  sale_order_id?: number;
  invoice_id?: number;
  invoice_number?: string;
  amount?: number;
  payment_method?: string;
  reference_number?: string;
  paid_at?: string | null;
  status?: string;
  pdf_path?: string;
  created_at?: string;
}

export function invoiceIdOf(invoice?: InvoiceDoc | null, fallbackId?: number | null): number | null {
  const id = invoice?.id ?? invoice?.invoice_id ?? fallbackId;
  return typeof id === "number" && id > 0 ? id : null;
}

export function hasInvoiceDoc(invoice?: InvoiceDoc | null, fallbackId?: number | null): boolean {
  return invoiceIdOf(invoice, fallbackId) != null;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadInvoicePdf(opts: {
  invoiceId?: number | null;
  saleOrderId: number;
  isCustomer?: boolean;
  invoiceNumber?: string | null;
}) {
  const { invoiceId, saleOrderId, isCustomer, invoiceNumber } = opts;
  let blob: Blob;
  if (invoiceId) {
    blob = await WebService.downloadFileAPI({
      action: `/v1/door-management/invoices/${invoiceId}/pdf`,
      method: "GET",
    });
  } else {
    const base = isCustomer
      ? "/v1/door-management/saleorders/my-saleorders"
      : "/v1/door-management/saleorders";
    blob = await WebService.downloadFileAPI({
      action: `${base}/${saleOrderId}/view-invoice`,
      method: "GET",
    });
  }
  const name = invoiceNumber
    ? `Invoice_${invoiceNumber}.pdf`
    : `Invoice_${saleOrderId}.pdf`;
  downloadBlob(blob, name);
}

export async function downloadReceiptPdf(opts: {
  receiptId: number;
  receiptNumber?: string | null;
}) {
  const blob = await WebService.downloadFileAPI({
    action: `/v1/door-management/receipts/${opts.receiptId}/pdf`,
    method: "GET",
  });
  const name = opts.receiptNumber
    ? `Receipt_${opts.receiptNumber}.pdf`
    : `Receipt_${opts.receiptId}.pdf`;
  downloadBlob(blob, name);
}

export async function generateReceiptForPayment(paymentId: number, body?: Record<string, unknown>) {
  return WebService.postAPI<any>({
    action: `/v1/door-management/payments/${paymentId}/generate-receipt`,
    body: body || {},
  });
}

export async function fetchSaleOrderReceipts(saleOrderId: number) {
  return WebService.getAPI<any>({
    action: `/v1/door-management/saleorders/${saleOrderId}/receipts`,
  });
}

export async function fetchInvoice(invoiceId: number) {
  return WebService.getAPI<any>({
    action: `/v1/door-management/invoices/${invoiceId}`,
  });
}

export async function fetchReceipt(receiptId: number) {
  return WebService.getAPI<any>({
    action: `/v1/door-management/receipts/${receiptId}`,
  });
}

export async function fetchSaleOrder(saleOrderId: number) {
  return WebService.getAPI<any>({
    action: `/v1/door-management/saleorders/${saleOrderId}`,
  });
}
